import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { useSessionStore } from '@/store/session';

import ErrorPage from '@/components/404.vue';
import Dashboard from '@/pages/Dashboard.vue';
import About from '@/pages/About.vue';
import UserList from '@/pages/UserList.vue';
import UserForm from '@/pages/UserForm.vue';
import Products from '@/pages/ProductList.vue';
import ProductForm from '@/pages/ProductForm.vue';
import Login from '@/pages/Login.vue';
import ChangePassword from '@/components/ChangePassword.vue';
import AccountList from '@/pages/AccountList.vue';
import AccountForm from '@/pages/AccountForm.vue';
import LaverieList from '@/pages/LaverieList.vue';
import TaskListTable from '@/pages/TaskListTable.vue';
import LaverieExpansionPanel from '@/pages/LaverieExpansionPanel.vue';
import EditionForm from '@/pages/EditionForm.vue';
import SparepartForm from '@/pages/SparepartForm.vue';
import SparepartList from '@/pages/SparepartList.vue';
import TaskTable from '@/pages/TaskTable.vue';
import TaskForm from '@/pages/TaskForm.vue';
import PanneForm from '@/pages/PanneForm.vue';
import AvenantForm from '@/pages/AvenantForm.vue';
import TaskListEditor from '@/pages/TaskListEditor.vue';
import VehicleForm from '@/pages/VehicleForm.vue';
import VehicleList from '@/pages/VehicleList.vue';
import Metadata from '@/pages/Metadata.vue';
import InterventionTemplateEditor from '@/pages/InterventionTemplateEditor.vue';
import InterventionTemplateList from '@/pages/InterventionTemplateList.vue';
import OnGoingInterventionEditor from '@/pages/OnGoingInterventionEditor.vue';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    roles?: string[];
    props?: Record<string, any> | boolean | ((route: RouteLocationNormalized) => Record<string, any>);
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/404',
    component: ErrorPage,
    name: 'ErrorPage'
  },
  {
    path: '/dashboard',
    component: Dashboard,
    name: 'dashboard',
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/about',
    component: About,
    name: 'about',
    meta: {
      requiresAuth: true
    },
  },
  {
    path: '/newaccount',
    component: AccountForm,
    name: 'newaccount',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_ACCOUNT"]
    },
  },
  {
    path: '/accounts',
    component: AccountList,
    name: 'accounts',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_ACCOUNT"]
    },
  },
  {
    path: '/accounts/:id',
    component: AccountForm,
    name: 'account',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_ACCOUNT"]
    },
  },
  {
    path: '/newlaverie',
    component: LaverieExpansionPanel,
    name: 'newlaverie',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_LAVERIE"]
    },
  },
  {
    path: '/accounts/:accountId/newlaverie',
    component: LaverieExpansionPanel,
    name: 'newlaverieforaccount',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_ACCOUNT", "ROLE_LAVERIE"]
    },
  },
  {
    path: '/laveries',
    component: LaverieList,
    name: 'laveries',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_LAVERIE"]
    },
  },
  {
    path: '/laveries/:id',
    component: LaverieExpansionPanel,
    name: 'laverie',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_LAVERIE"]
    },
  },
  {
    path: '/editions',
    component: EditionForm,
    name: 'editions',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_EDITION"]
    },
  },
  {
    path: '/newuser',
    component: UserForm,
    name: 'newuser',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_USER"]
    },
  },
  {
    path: '/users',
    component: UserList,
    name: 'users',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_USER"]
    },
  },
  {
    path: '/users/:id',
    component: UserForm,
    name: 'user',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_USER"]
    },
  },
  {
    path: '/newproduct',
    component: ProductForm,
    name: 'newproduct',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_PRODUCT"]
    },
  },
  {
    path: '/products',
    component: Products,
    name: 'products',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN',"ROLE_PRODUCT"]
    },
  },
  {
    path: '/products/:id',
    component: ProductForm,
    name: 'product',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN',"ROLE_PRODUCT"]
    },
  },
  {
    path: '/newtasklist',
    component: TaskListEditor,
    name: 'newtasklist',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/tasklists',
    component: TaskListTable,
    name: 'tasklists',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN',"ROLE_TOURNEE"]
    },
  },
  {
    path: '/tasklists/:id',
    component: TaskListEditor,
    name: 'tasklist',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/tasks',
    component: TaskTable,
    name: 'tasks',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/tasks/:id',
    component: TaskForm,
    name: 'task',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
    props: (route) => ({
      cardClass: 'grey lighten-4 elevation-0',
      id: route.params.id
    })
  },
  {
    path: '/newtask',
    component: TaskForm,
    name: 'newtask',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
    props: {
      cardClass: 'grey lighten-4 elevation-0'
    }
  },
  {
    path: '/newsparepart',
    component: SparepartForm,
    name: 'newsparepart',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_SPARPART"]
    },
  },
  {
    path: '/spareparts',
    component: SparepartList,
    name: 'spareparts',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_SPARPART"]
    },
  },
  {
    path: '/spareparts/:id',
    component: SparepartForm,
    name: 'sparepart',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_SPARPART"]
    },
  },
  {
    path: '/products/:productId/pannes/:id',
    component: PanneForm,
    name: 'panne',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_PRODUCT"]
    },
  },
  {
    path: '/products/:productId/pannes/new',
    component: PanneForm,
    name: 'newpanne',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_PRODUCT"]
    },
  },
  {
    path: '/laveries/:laverieId/avenants/:id',
    component: AvenantForm,
    name: 'avenant',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_LAVERIE"]
    },
  },
  {
    path: '/laveries/:laverieId/avenants/new',
    component: AvenantForm,
    name: 'newavenant',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_LAVERIE"]
    },
  },
  {
    path: '/vehicles/:id',
    component: VehicleForm,
    name: 'vehicle',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/newvehicle',
    component: VehicleForm,
    name: 'NewVehicle',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/vehicles',
    component: VehicleList,
    name: 'vehicles',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TOURNEE"]
    },
  },
  {
    path: '/on-going-interventions/:id',
    component: OnGoingInterventionEditor,
    name: 'ongoinginterventions',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN', "ROLE_TECHNICIAN"]
    },
  },
  {
    path: '/intervention-templates',
    component: InterventionTemplateList,
    name: 'interventiontemplates',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN']
    },
  },
  {
    path: '/intervention-templates/:id',
    component: InterventionTemplateEditor,
    name: 'InterventionTemplateEditor',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN']
    },
  },
   {
    path: '/metadata',
    component: Metadata,
    name: 'metadata',
    meta: {
      requiresAuth: true,
      roles: ['ROLE_ADMIN']
    },
  },
  {
    path: '/login',
    component: Login,
    name: 'login'
  },
  {
    path: '/changePassword',
    component: ChangePassword,
    name: 'ChangePassword'
  },
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Optional: Scroll behavior
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

router.beforeEach((to, from, next) => {
  const sessionStore = useSessionStore();

  // Authentication
   const requiresAuth = to.matched.some(
    record => record.meta.requiresAuth
  );

  if (requiresAuth && !sessionStore.isSignedIn) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  // Authorization
  const requiredRoles = to.matched
    .flatMap(record => record.meta.roles ?? []);
  if (requiredRoles.length > 0) {
    const hasAccess = requiredRoles.some(role =>
      sessionStore.userRoles.includes(role)
    );

    if (!hasAccess) {
      next({ name: 'ErrorPage' });
      return;
    }
  }

  next();
});

export default router;
